import type { Category, Subcategory } from "../types";
import type { HomeTrendingMonarchCategoriesBlock } from "../types";
import { nonBlankString } from "@/utils/nonBlankString";

function subcategoryKey(label: string) {
  return label.trim().toLowerCase();
}

function withFallbackCta(
  subcategory: Subcategory,
  fallback?: Subcategory,
): Subcategory {
  if (!fallback) {
    return subcategory;
  }

  return {
    ...subcategory,
    searchUrl: subcategory.searchUrl ?? fallback.searchUrl,
    ctaLabel: subcategory.ctaLabel ?? fallback.ctaLabel,
    ctaUrl: subcategory.ctaUrl ?? fallback.ctaUrl,
  };
}

function mergeSubcategoryCtas(
  subcategories: Subcategory[] | null | undefined,
  fallbackSubcategories: Subcategory[] | null | undefined,
) {
  return subcategories?.map((subcategory) =>
    withFallbackCta(
      subcategory,
      fallbackSubcategories?.find(
        (candidate) =>
          subcategoryKey(candidate.subcategoryLabel) ===
          subcategoryKey(subcategory.subcategoryLabel),
      ),
    ),
  );
}

function mergeCategoryCtas(
  categories: Category[],
  fallbackCategories: Category[],
): Category[] {
  return categories.map((category) => ({
    ...category,
    subcategories: mergeSubcategoryCtas(
      category.subcategories,
      fallbackCategories.find(
        (candidate) =>
          candidate.categoryKey.toLowerCase() ===
          category.categoryKey.toLowerCase(),
      )?.subcategories,
    ),
  }));
}

function mapCustomCategories(
  trendingCategories: HomeTrendingMonarchCategoriesBlock | undefined,
) {
  return trendingCategories?.items
    ?.map((item) => {
      if (!item.category) {
        return null;
      }
      const subcategories = item.category?.subcategories ?? [];
      return {
        ...item.category,
        productType: item.category.productType ?? "",
        categoryLabel: item.category.name,
        categoryKey: item.category.categoryKey,
        additionalProductTypes: item.category.additionalProductTypes,
        subcategories: subcategories.map((s) => ({
          subcategoryLabel: s.name,
          customQuery: s.customQuery,
          searchUrl: nonBlankString(s.searchUrl),
          ctaLabel: nonBlankString(s.ctaLabel),
          ctaUrl: nonBlankString(s.ctaUrl),
        })),
      };
    })
    .filter(Boolean);
}

export default function mergeCategories(
  categories: Category[],
  trendingCategories: HomeTrendingMonarchCategoriesBlock | undefined,
): Category[] {
  if (
    !trendingCategories ||
    !trendingCategories.items ||
    trendingCategories.items.length === 0
  ) {
    return categories;
  }

  const mappedCustomCategories = mapCustomCategories(trendingCategories) ?? [];

  const monarchCategories = mergeCategoryCtas(
    mappedCustomCategories as unknown as Category[],
    categories,
  );
  // Prevent duplicate categories, monarch categories always override cms categories
  const uniqueCategories = categories.filter((category) => {
    return !monarchCategories.some(
      (c) => c.categoryKey.toLowerCase() === category.categoryKey.toLowerCase(),
    );
  });

  if (trendingCategories.replaceContent) {
    return monarchCategories;
  }

  if (trendingCategories.appendToEnd) {
    return [...uniqueCategories, ...monarchCategories];
  }

  return [...monarchCategories, ...uniqueCategories];
}
